import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { NavigationFailure, reasonFromError } from "@/components/NavigationFailure";
import { useCustomerView } from "@/context/CustomerViewContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Pencil, Truck } from "lucide-react";
import { LinesSummary, requestColorsText, requestFormatText } from "@/components/custom/CustomStudioLines";
import { AttachmentLink, SampleImage } from "@/components/custom/AttachmentLink";
import { RevisionForm } from "@/components/custom/RevisionForm";
import { RequestTimeline } from "@/components/custom/RequestTimeline";
import { FinalSpecVersions } from "@/components/custom/FinalSpecVersions";
import { STAGE_CLASS, StatusChip } from "@/components/custom/stageColors";
import { Textarea } from "@/components/ui/textarea";
import { ReorderDesign } from "@/components/custom/ReorderDesign";

const PROGRESS = [
  "Request received", "Under review", "Development proposal", "Sample in development",
  "Sample ready — awaiting dispatch to Zelligio", "In transit to Zelligio", "Received by Zelligio", "Sample shipped to you",
  "Awaiting your review", "Approved for production", "In production", "Production complete",
  "In transit to destination", "Arrived — preparing delivery", "Ready for delivery", "Shipped to you", "Delivered — project complete",
];

export default function CustomStudioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customerView } = useCustomerView();
  const [req, setReq] = useState(null);
  const [navFail, setNavFail] = useState(null);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesText, setChangesText] = useState("");
  const [acting, setActing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/dealer/custom-studio/${id}${customerView ? "?customer_view=1" : ""}`)
    .then((r) => setReq(r.data))
    .catch((e) => setNavFail(reasonFromError(e)));  // NAV-009
  useEffect(() => { load(); }, [id, customerView]);
  // ID-033: after the Stripe redirect (success or cancel) the server verifies with the provider — the redirect alone is never trusted
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).get("checkout")) return;
    api.post(`/dealer/custom-studio/${id}/payment/reconcile`).then((r) => setReq(r.data)).catch(() => {});
  }, [id]);

  if (navFail) return <NavigationFailure reason={navFail} moduleLabel="Custom Studio" modulePath="/dealer/custom-studio" />;
  if (!req) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;

  const proposal = req.proposal;
  const progressIdx = PROGRESS.indexOf(req.dealer_status);
  const finalPending = ["AWAITING_DEALER_REVIEW", "APPROVED"].includes(req.status) && req.final_spec && !req.final_spec.approved_at;
  const finalV = req.final_spec ? req.final_spec.final_version || (req.final_spec_history || []).length + 1 : 0;
  const changesPending = !!req.final_spec?.changes_requested_at;

  const act = async (fn, msg) => {
    setActing(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(false);
    }
  };

  const pay = () => act(async () => {
    const { data } = await api.post(`/dealer/custom-studio/${id}/proposal/checkout`, { origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/dealer/custom-studio/${id}/attachments`, form);
      toast.success("Attachment added");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const decision = (action, extra = {}) => act(async () => {
    await api.post(`/dealer/custom-studio/${id}/sample-decision`, { action, ...extra });
    setReviseOpen(false);
  }, action === "APPROVE" ? "Sample approved" : "Revision sent to Zelligio");

  return (
    <div className="max-w-3xl" data-testid="custom-studio-detail">
      <Button variant="ghost" onClick={() => navigate("/dealer/custom-studio")} data-testid="back-to-custom-studio" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> Custom Studio
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-xs text-muted-foreground" data-testid="custom-revision-ref">{req.request_number} · current: {req.revision_ref}</p>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight text-charcoal">{req.design_name || req.product_type}</h1>
          {req.project_name && <p className="mt-1 text-sm text-warmgrey">Project: {req.project_name}</p>}
          {req.reorder_of && <p className="mt-1 text-xs text-warmgrey" data-testid="custom-reorder-of">Reorder of approved design {req.reorder_of.request_number}{req.reorder_of.notes ? ` — ${req.reorder_of.notes}` : ""}</p>}
        </div>
        <StatusChip status={req.status} label={req.dealer_status} testid="custom-detail-status" />
        {req.minimum_review && req.minimum_review.status !== "PENDING" && (
          <p className="w-full rounded-md border border-border bg-sand p-3 text-xs text-charcoal" data-testid="custom-below-minimum-decision">
            Below-minimum request {req.minimum_review.status === "ACCEPTED" ? "accepted by Zelligio" : "declined by Zelligio"}
            {req.minimum_review.status === "ACCEPTED" && req.minimum_review.surcharge_note ? ` — surcharge/terms: ${req.minimum_review.surcharge_note}` : ""}
            {req.minimum_review.note ? `. ${req.minimum_review.note}` : ""}
          </p>
        )}
        {req.minimum_review?.status === "PENDING" && (
          <p className="w-full rounded-md border border-status-reserved/40 bg-sand p-3 text-xs text-charcoal" data-testid="custom-below-minimum-notice">
            Below the 300 sq ft guideline — waiting for Zelligio review. Zelligio will accept it (possibly with a surcharge) or decline it before any proposal.
          </p>
        )}
      </div>

      {!customerView && <ReorderDesign req={req} />}

      {/* progress rail */}
      <div className="mt-6 flex flex-wrap gap-1.5" data-testid="custom-progress">
        {PROGRESS.map((label, i) => (
          <span key={label} data-state={i < progressIdx ? "done" : i === progressIdx ? "current" : "future"} className={`rounded-full px-2.5 py-1 text-[10px] font-medium ${
            i < progressIdx ? STAGE_CLASS.done : i === progressIdx ? STAGE_CLASS.current : STAGE_CLASS.future}`}>
            {label}
          </span>
        ))}
        {["ON_HOLD", "LOST", "DECLINED", "CANCELLED"].includes(req.status) && <StatusChip status={req.status} label={req.dealer_status} testid="custom-progress-closed" />}
      </div>

      {/* request summary */}
      <div className="mt-6 rounded-md border border-border bg-white p-6" data-testid="custom-summary">
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Your request</p>
        <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div className="flex justify-between gap-3"><dt className="text-warmgrey">Color</dt><dd className="text-charcoal" data-testid="custom-summary-colors">{requestColorsText(req)}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-warmgrey">Format</dt><dd className="text-charcoal" data-testid="custom-summary-format">{requestFormatText(req)}</dd></div>
          <div className="flex justify-between gap-3"><dt className="text-warmgrey">Quantity</dt><dd className="text-charcoal num">{req.quantity_sqft} sq ft</dd></div>
          {req.dimensions && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Dimensions</dt><dd className="text-charcoal">{req.dimensions}</dd></div>}
          {req.application && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Application</dt><dd className="text-charcoal">{req.application}</dd></div>}
          {req.desired_delivery_date && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Desired delivery</dt><dd className="text-charcoal num">{req.desired_delivery_date}</dd></div>}
          {req.project_name && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Project</dt><dd className="text-charcoal">{req.project_name}</dd></div>}
        </dl>
        <LinesSummary lines={req.lines} totals={req.line_totals} />
        {req.description && <p className="mt-3 border-t border-border pt-3 text-sm text-warmgrey">{req.description}</p>}
        <div className="mt-4 border-t border-border pt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Attachments</p>
          {/* RC51-QA-005: files are shown under the product line they belong to */}
          {(req.lines || []).map((l, li) => {
            const mine = (req.attachments || []).filter((a) => a.line_index === li);
            return mine.length ? (
              <div key={li} className="mt-2" data-testid={`custom-line-attachments-${li}`}>
                <p className="text-[11px] text-warmgrey">Line {li + 1} — {l.summary}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {mine.map((a) => (
                    <AttachmentLink key={a.idx} path={`/dealer/custom-studio/${id}/files/${a.idx}`} filename={a.filename}
                      className="rounded-md border border-border bg-sand px-3 py-1.5 text-xs text-charcoal hover:border-terracotta" testid={`custom-attachment-${a.idx}`} />
                  ))}
                </div>
              </div>
            ) : null;
          })}
          <div className="mt-2 flex flex-wrap gap-2" data-testid="custom-attachments">
            {(req.attachments || []).filter((a) => a.line_index == null).map((a) => (
              <AttachmentLink key={a.idx} path={`/dealer/custom-studio/${id}/files/${a.idx}`} filename={a.filename}
                className="rounded-md border border-border bg-sand px-3 py-1.5 text-xs text-charcoal hover:border-terracotta" testid={`custom-attachment-${a.idx}`} />
            ))}
            <label className="cursor-pointer rounded-md border border-dashed border-terracotta px-3 py-1.5 text-xs font-medium text-terracotta hover:bg-[#BC6955]/5" data-testid="custom-upload-label">
              {uploading ? "Uploading…" : "+ Add file"}
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={upload} className="hidden" data-testid="custom-upload-input" />
            </label>
          </div>
        </div>
      </div>

      {/* proposal */}
      {proposal && proposal.total_due != null && (
        <div className="mt-6 rounded-md border border-terracotta/40 bg-white p-6" data-testid="custom-proposal">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{proposal.fee_type === "SAMPLE_DEVELOPMENT" ? "Development proposal" : proposal.fee_type === "REBOOKING" ? "Rebooking fee" : "Revision fee"}</p>
          <p className="mt-2 text-sm text-warmgrey">{proposal.summary}</p>
          {proposal.reason && <p className="mt-1 text-sm text-charcoal" data-testid="custom-fee-reason">Reason: {proposal.reason}</p>}
          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-warmgrey">Sample development</dt><dd className="num text-charcoal">${proposal.dev_fee?.toFixed(2)}</dd></div>
            <div className="flex justify-between"><dt className="text-warmgrey">Sample shipping & handling</dt><dd className="num text-charcoal">${proposal.sample_shipping?.toFixed(2)}</dd></div>
            <div className="flex justify-between border-t border-border pt-2"><dt className="font-medium text-charcoal">{proposal.paid ? "Total paid" : proposal.waived ? "Total (waived)" : "Total due now"}</dt><dd className="num font-semibold text-charcoal" data-testid="custom-proposal-total">${proposal.total_due?.toFixed(2)}</dd></div>
            {proposal.prod_lead_time && <div className="flex justify-between pt-1"><dt className="text-warmgrey">Estimated production lead time</dt><dd className="text-charcoal">{proposal.prod_lead_time}</dd></div>}
            {proposal.prod_price_estimate && <div className="flex justify-between"><dt className="text-warmgrey">Estimated production pricing</dt><dd className="text-charcoal" data-testid="custom-prod-price-estimate">{isNaN(Number(proposal.prod_price_estimate)) ? proposal.prod_price_estimate : `$${Number(proposal.prod_price_estimate).toFixed(2)} / sq ft`}</dd></div>}
          </dl>
          {req.status === "PAYMENT_REQUIRED" && !proposal.paid && !proposal.waived && proposal.payment_status !== "PROCESSING" && (
            <div className="mt-4 flex gap-3">
              <Button onClick={pay} disabled={acting} data-testid="custom-pay-button" className="bg-terracotta text-white hover:bg-terracotta-hover">
                {proposal.payment_status === "PENDING_CHECKOUT" || proposal.payment_status === "NOT_COMPLETED" ? "Resume payment" : proposal.reopened ? "Resume request and pay" : "Approve & pay"}
              </Button>
              <Button variant="outline" onClick={() => act(() => api.post(`/dealer/custom-studio/${id}/proposal/decline`), "Proposal declined")}
                disabled={acting} data-testid="custom-decline-button">
                Decline
              </Button>
            </div>
          )}
          {proposal.payment_status === "PROCESSING" && <p className="mt-3 text-xs font-medium text-status-reserved" data-testid="custom-payment-processing">Payment submitted — waiting for the payment provider to confirm. No further payment is needed.</p>}
          {["PENDING_CHECKOUT", "NOT_COMPLETED"].includes(proposal.payment_status) && !proposal.paid && <p className="mt-3 text-xs text-warmgrey" data-testid="custom-payment-not-completed">Payment not completed yet — you can safely resume it; you will only be charged once.</p>}
          {proposal.paid && <p className="mt-3 text-sm font-medium text-status-available" data-testid="custom-payment-received">{proposal.fee_type === "SAMPLE_DEVELOPMENT" ? "Sample development payment" : "Fee payment"} received: ${proposal.amount_paid?.toFixed(2)}{proposal.paid_at ? ` on ${new Date(proposal.paid_at).toLocaleDateString()}` : ""} — no payment is due.</p>}
          {proposal.waived && <p className="mt-3 text-xs text-status-available">Fee waived by Zelligio — no payment is due.</p>}
          {req.status === "DECLINED" && !proposal.paid && !proposal.waived && <p className="mt-3 text-xs text-warmgrey" data-testid="custom-fee-declined">You declined this fee. It stays on record; Zelligio can reopen the request with a corrected or waived fee.</p>}
        </div>
      )}

      {/* sample images curated by Zelligio */}
      {(req.sample_images || []).length > 0 && (
        <div className="mt-6 rounded-md border border-border bg-white p-6" data-testid="custom-sample-images">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Sample photos from Zelligio</p>
          <div className="mt-3 flex flex-wrap gap-3">
            {req.sample_images.map((a) => (
              <SampleImage key={a.idx} path={`/dealer/custom-studio/${id}/files/${a.idx}?sample=1`} a={a} />
            ))}
          </div>
        </div>
      )}

      {/* tracking (only once Zelligio ships to the dealer) */}
      {req.tracking && (
        <div className="mt-6 rounded-md border border-border bg-white p-6" data-testid="custom-tracking">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-warmgrey"><Truck className="h-4 w-4 text-terracotta" /> Tracking</p>
          <p className="mt-2 text-sm text-charcoal" data-testid="custom-tracking-details">{req.tracking.carrier} · <span className="num" data-testid="custom-tracking-number">{req.tracking.tracking_number}</span></p>
          {req.tracking.shipped_at && <p className="mt-1 text-xs text-warmgrey">Shipped {new Date(req.tracking.shipped_at).toLocaleDateString()}</p>}
          {req.tracking.tracking_url && (
            <a href={req.tracking.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="custom-tracking-link"
              className="mt-3 inline-flex h-9 items-center rounded-md bg-terracotta px-3 text-xs font-semibold text-white hover:bg-terracotta-hover">Track shipment</a>
          )}
          {req.status === "DELIVERED" && (
            <p className="mt-3 flex items-center gap-2 text-sm font-medium text-status-available" data-testid="custom-delivered-banner">
              <CheckCircle2 className="h-4 w-4" /> Delivered{req.delivered_at ? ` ${new Date(req.delivered_at).toLocaleDateString()}` : ""} — project complete
            </p>
          )}
        </div>
      )}

      {/* dealer sample decision */}
      {req.status === "AWAITING_DEALER_REVIEW" && !finalPending && (
        <div className="mt-6 rounded-md border border-terracotta/40 bg-white p-6" data-testid="custom-sample-decision">
          <p className="text-sm font-medium text-charcoal">How is the sample?</p>
          <div className="mt-3 flex gap-3">
            <Button onClick={() => decision("APPROVE")} disabled={acting} data-testid="custom-approve-sample" className="bg-status-available text-white hover:opacity-90">
              <CheckCircle2 className="mr-2 h-4 w-4" /> Approve sample
            </Button>
            <Button variant="outline" onClick={() => setReviseOpen(true)} disabled={acting} data-testid="custom-revise-sample">
              <Pencil className="mr-2 h-4 w-4" /> Request revision
            </Button>
          </div>
        </div>
      )}

      {reviseOpen && <RevisionForm req={req} busy={acting} onSubmit={(extra) => decision("REVISE", extra)} onCancel={() => setReviseOpen(false)} />}

      {/* final production specification approval */}
      {finalPending && (
        <div className="mt-6 rounded-md border-2 border-terracotta bg-white p-6" data-testid="custom-final-spec">
          <p className="text-xs font-semibold uppercase tracking-wider text-terracotta">Final production specification — your approval starts production</p>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-3"><dt className="text-warmgrey">Specification version</dt><dd className="num text-charcoal" data-testid="final-spec-version">v{finalV}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-warmgrey">Final quantity</dt><dd className="num font-medium text-charcoal" data-testid="final-qty">{req.final_spec.data.final_quantity_sqft} sq ft</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-warmgrey">Approved sample</dt><dd className="text-charcoal">{req.final_spec.approved_sample_ref}</dd></div>
            {req.final_spec.data.color_mode === "CUSTOM" && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Custom color</dt><dd className="text-charcoal">{req.final_spec.data.custom_color_ref}</dd></div>}
            {req.final_spec.data.catalog_color_code && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Catalog color</dt><dd className="text-charcoal">{req.final_spec.data.catalog_color_code}</dd></div>}
            {req.final_spec.data.custom_shape_desc && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Shape</dt><dd className="text-charcoal">{req.final_spec.data.custom_shape_desc}</dd></div>}
            {req.final_spec.data.dimensions && <div className="flex justify-between gap-3"><dt className="text-warmgrey">Dimensions</dt><dd className="text-charcoal">{req.final_spec.data.dimensions}</dd></div>}
            {req.final_spec.data.special_instructions && <div className="col-span-full"><dt className="text-warmgrey">Special instructions</dt><dd className="mt-1 text-charcoal">{req.final_spec.data.special_instructions}</dd></div>}
          </dl>
          <p className="mt-4 rounded-md bg-sand p-3 text-xs text-warmgrey">
            Production uses ONLY this final approved specification. Please confirm every detail — this cannot be changed once approved.
          </p>
          {changesPending ? (
            <p className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-charcoal" data-testid="custom-final-changes-pending">You requested changes to v{finalV}. Zelligio is preparing the next version — production has not started and nothing is approved.</p>
          ) : (
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={() => act(() => api.post(`/dealer/custom-studio/${id}/final-approval`, { final_version: finalV }), "Production specification approved")}
              disabled={acting} data-testid="custom-final-approve" className="bg-terracotta text-white hover:bg-terracotta-hover">
              Approve sample & production specification
            </Button>
            <Button variant="outline" onClick={() => setChangesOpen(true)} disabled={acting} data-testid="custom-final-request-changes">Request changes</Button>
          </div>
          )}
          {changesOpen && (
            <div className="mt-4" data-testid="custom-final-changes-form">
              <Textarea value={changesText} onChange={(e) => setChangesText(e.target.value)} placeholder="Describe the corrections or changes you need" className="min-h-[90px] bg-white" data-testid="custom-final-changes-input" />
              <div className="mt-2 flex gap-3">
                <Button disabled={acting || changesText.trim().length < 3} data-testid="custom-final-changes-submit" className="bg-charcoal text-white hover:bg-charcoal/90"
                  onClick={() => act(async () => { await api.post(`/dealer/custom-studio/${id}/final-spec/changes`, { text: changesText }); setChangesOpen(false); setChangesText(""); }, "Changes sent to Zelligio")}>
                  Send changes to Zelligio
                </Button>
                <Button variant="ghost" onClick={() => setChangesOpen(false)} data-testid="custom-final-changes-cancel">Cancel</Button>
              </div>
              <p className="mt-2 text-xs text-warmgrey">Production does not start and nothing is charged while Zelligio reviews your changes.</p>
            </div>
          )}
        </div>
      )}

      {/* specification history */}
      {(req.spec_versions || []).length > 0 && (
        <div className="mt-6 rounded-md border border-border bg-white p-6" data-testid="custom-spec-history">
          <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Specification history</p>
          <ul className="mt-3 space-y-2">
            {req.spec_versions.map((v) => (
              <li key={v.version} className="text-sm" data-testid={`spec-v${v.version}`}>
                <div className="flex flex-wrap items-center gap-3">
                <span className="num font-medium text-charcoal">{v.revision_ref}</span>
                <span className="text-warmgrey">{v.note}</span>
                <span className="text-xs text-muted-foreground num">{new Date(v.created_at).toLocaleString()}</span>
                {req.final_spec?.spec_version === v.version && <span className="badge-status badge-available">production spec</span>}
                {v.revision_ref === req.revision_ref && <span className="badge-status badge-neutral">current</span>}
                </div>
                {Object.entries(v.changed || {}).map(([k, c]) => <p key={k} className="ml-1 text-xs text-warmgrey" data-testid={`spec-v${v.version}-change-${k}`}>{k.replace("_sqft", "").replaceAll("_", " ")}: {c.from || "-"} → {c.to || "-"}</p>)}
              </li>
            ))}
          </ul>
          {req.final_spec && <FinalSpecVersions base={`/dealer/custom-studio/${id}`} refreshKey={req.updated_at} />}
        </div>
      )}
      <RequestTimeline base={`/dealer/custom-studio/${id}`} requestNumber={req.request_number} refreshKey={req.updated_at} />
    </div>
  );
}
